import { describeError } from "../../api/client";
import { Combobox, type ComboboxOption } from "../../components/Combobox";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { useQuery } from "../../state/useQuery";
import { getTemplateOptions } from "../templates/api";
import { isSelectedOutsideWindow } from "./selectorFiltering";

/**
 * Recognizable-by-name template selector for the campaign editor
 * (redesign-panel-hubspot-mailgun, PR 9, task 9.6/9.7; design.md §5.2:
 * "Template and segment are searchable/selectable by name with secondary
 * metadata (template product/version/active state)... Selection stores IDs
 * but never requires typing them. Inactive templates are unavailable with
 * explanation. Options are fetched once per editor from existing bounded
 * lists"). Rendered with the shared searchable `Combobox` (panel feedback
 * 2026-10-30): the list is filterable by name and escapes the modal body.
 */
export function TemplateSelector({
  id,
  value,
  onChange,
  disabled,
  activeOnly = false,
}: {
  id: string;
  value: string;
  onChange: (templateId: string) => void;
  disabled?: boolean;
  /**
   * Prototype v3 (Nueva campaña): "Solo se muestran plantillas activas." --
   * hide inactive templates entirely instead of listing them as disabled.
   * Editors that must still show a campaign's current (possibly inactive)
   * template keep the default.
   */
  activeOnly?: boolean;
}) {
  const { data, error, loading, refetch } = useQuery({
    key: ["template-options"],
    queryFn: () => getTemplateOptions(),
  });

  const selectedOutsideWindow = isSelectedOutsideWindow(value, data, (option) => option.templateId);

  const options: ComboboxOption[] = [
    ...(selectedOutsideWindow
      ? [{ value, label: "Plantilla actual (fuera de la lista visible)", disabled: true }]
      : []),
    ...(data ?? [])
      .filter((option) => !activeOnly || option.activo)
      .map((option) => ({
      value: option.templateId,
      label: option.nombre,
      description: `${option.producto} · v${option.version}${option.activo ? "" : " · inactiva, no disponible"}`,
      disabled: !option.activo,
    })),
  ];

  return (
    <div className="v2-template-selector">
      {loading && <p role="status">Cargando plantillas…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudieron cargar las plantillas.")}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          <label htmlFor={id}>Plantilla</label>
          {data.length === 0 ? (
            <FeedbackPanel variant="empty" message="No hay plantillas disponibles." />
          ) : (
            <Combobox
              id={id}
              value={value}
              disabled={disabled}
              required
              onChange={onChange}
              options={options}
              placeholder="Selecciona una plantilla"
            />
          )}
          {selectedOutsideWindow && (
            <p className="v2-template-selector__outside-window">
              La plantilla asignada actualmente no aparece en esta lista acotada; seleccione otra para cambiarla.
            </p>
          )}
        </>
      )}
    </div>
  );
}
